import { useAuthStore } from '@client/stores/AuthStore';
import { expireSession } from '@client/utils/auth/sessionExpiry';
import type { Pathname } from '@shared/navigation/routes.gen';
import { generateHref } from '@shared/utils/core/navigation';

import { type AuthSessionSnapshot, isTrialExpiredError } from './error-classification';
import { readOrgIdFromUrl, resolveRequestOrgId } from './orgIdHeader';
import { isUnauthorizedTrpcError, shouldExpireSessionOnUnauthorized } from './sessionHandling';

const TRIAL_EXPIRED_PATH = '/trial-expired' satisfies Pathname;

/**
 * Build an {@link AuthSessionSnapshot} from the live Zustand auth store — the one
 * adapter between the store singleton and the pure predicates in
 * `error-classification.ts`, which stay decoupled from it so they remain
 * unit-testable.
 *
 * Owned here rather than in `client.tsx` so the cache handlers and
 * `runImperativeMutation` classify session expiry from the same input: a field
 * added to the snapshot then reaches every caller at once.
 */
export function getAuthSessionSnapshot(): AuthSessionSnapshot {
    const authStore = useAuthStore.getState();
    return {
        authTokenExpired: authStore.authTokenExpired,
        isTokenExpired: () => authStore.isTokenExpired(),
        isLoggingOut: authStore.isLoggingOut,
        isRedirectingToLogin: authStore.isRedirectingToLogin,
    };
}

/**
 * The app-wide reaction to a server-returned denial, owned in one place so every
 * caller applies the same auth rules.
 *
 * `MutationCache.onError` / `QueryCache.onError` run it for hook-driven traffic;
 * `runImperativeMutation` runs it for calls that outlive their React owner and
 * therefore never reach a cache handler (see `imperative-mutation.ts`). Keeping
 * it here rather than inline in `client.tsx` avoids each new caller re-deriving
 * "is this UNAUTHORIZED worth expiring the session for" for itself.
 */
export const handleGlobalUnauthorizedError = (error: unknown) => {
    if (isUnauthorizedTrpcError(error) && shouldExpireSessionOnUnauthorized(window.location.pathname)) {
        expireSession('[tRPC] Global UNAUTHORIZED error detected, triggering auth redirect');
        return;
    }

    // Route stable server-side denial codes to dedicated screens. authProcedure throws FORBIDDEN
    // with these messages when the org or user state changes mid-session (e.g. role removed,
    // payment lapsed, Light trial ended). The pathname guard keeps a screen's own probe from
    // reloading it in a loop.
    if (typeof error === 'object' && error !== null && 'data' in error) {
        const trpcError = error as { data?: { code?: string }; message?: string };
        if (trpcError.data?.code === 'FORBIDDEN') {
            if (trpcError.message === 'payment_required_for_login') {
                if (window.location.pathname !== '/payment-pending') {
                    window.location.href = '/payment-pending';
                }
            }
            if (isTrialExpiredError(error)) {
                if (window.location.pathname !== TRIAL_EXPIRED_PATH) {
                    window.location.href = trialExpiredHref();
                }
            }
            // no_role_assigned and other server denials surface via the existing error UI;
            // a dedicated redirect would orphan the user mid-flow. Leave for the per-screen handler.
        }
    }
};

/**
 * The expiry screen, naming the org the refused request went to so the screen checks that org
 * rather than whichever one the store holds. A cache handler cannot see a request's own
 * `orgIdOverride`, so this is the org the header link sends by default.
 */
function trialExpiredHref(): string {
    const { pendingOrgId, org } = useAuthStore.getState();
    const refusedOrgId = resolveRequestOrgId({
        pendingOrgId,
        storeOrgId: org?.id,
        urlOrgId: readOrgIdFromUrl(window.location.pathname),
    });
    return generateHref({
        baseUrl: '',
        to: TRIAL_EXPIRED_PATH,
        searchParams: refusedOrgId === undefined ? undefined : { orgId: refusedOrgId },
    });
}
